import { createAsyncThunk } from "@reduxjs/toolkit";
import { useCustomAxios } from "../../shared/lib/customAxios";
import { API_ENDPOINTS } from "../../shared/constants/constants";

export const createRFQApi = createAsyncThunk(
  "buyer/createRFQ",
  async (reqBody: any) => {
    const { axiosPost } = useCustomAxios();

    try {
      const response = await axiosPost(`${API_ENDPOINTS.createRFQ}`, reqBody);
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return {
        error: error.response.data.message,
        status: error.response.status,
      };
    }
  }
);

export const UpdateRFQApi = createAsyncThunk(
  "buyer/updateRFQ",
  async (reqBody: any) => {
    const { axiosPost } = useCustomAxios();

    try {
      const response = await axiosPost(`${API_ENDPOINTS.updateRFQ}`, reqBody);
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return {
        error: error.response.data.message,
        status: error.response.status,
      };
    }
  }
);

export const getRFQList = createAsyncThunk(
  "buyer/getRFQList",
  async (parameter: any) => {
    const { axiosGet } = useCustomAxios();
    let url = `${API_ENDPOINTS.getRFQList}`;

    Object.entries(parameter).forEach(
      ([key, value], index) =>
        (url = url + `${index > 0 ? "&" : "?"}${key}=${value}`)
    );

    try {
      const response = await axiosGet(url);
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return error.response.data;
    }
  }
);

export const getRFQDetails = createAsyncThunk(
  "buyer/getRFQDetails",
  async (id: any, { dispatch }) => {
    const { axiosGet } = useCustomAxios();
    let url = `${API_ENDPOINTS.getRFQ}/${id}`;

    try {
      const response = await axiosGet(url);
      let RFQstatus;
      if (response.status === 200) {
        RFQstatus = await dispatch(getRFQStatus(id)).unwrap();
      }
      return {
        data: { ...response.data, rfqData: RFQstatus.data },
        status: response.status,
      };
    } catch (error: any) {
      return error.response.data;
    }
  }
);

export const getRFQStatus = createAsyncThunk(
  "buyer/getRFQStatus",
  async (id: any) => {
    const { axiosGet } = useCustomAxios();
    let url = `${API_ENDPOINTS.getRFQStatus}/${id}/statuses`;

    try {
      const response = await axiosGet(url);
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return error.response.data;
    }
  }
);

export const getGenerateComparisonMatrix = createAsyncThunk(
  "buyer/getGenerateComparisonMatrix",
  async (data: any) => {
    const {id, queryParams} = data;
    const { axiosGet } = useCustomAxios();
    let url = `/order/request/${id}/toplowestquotes`;
    Object.entries(queryParams).forEach(
      ([key, value], index) =>
        (url = url + `${index > 0 ? '&' : '?'}${key}=${value}`)
    );
    try {
      const response = await axiosGet(url);
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return error.response.data;
    }
  }
);

export const updateQuoteApproval = createAsyncThunk(
  "buyer/updateQuoteApproval",
  async (reqBody: any) => {
    const { axiosPost } = useCustomAxios();

    try {
      const response = await axiosPost(
        "/order/request/quote/updateQuoteApproval",
        reqBody
      );
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return {
        error: error.response.data.message,
        status: error.response.status,
      };
    }
  }
);

export const uploadFile = createAsyncThunk(
  "buyer/uploadRFQItemsFile",
  async (reqBody: any) => {
    const { axiosPost } = useCustomAxios();

    try {
      const response = await axiosPost(`${API_ENDPOINTS.fileUpload}`, reqBody);
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return {
        error: error.response.data.message,
        status: error.response.status,
      };
    }
  }
);

export const downloadRequestItemsExcel = createAsyncThunk(
  "buyer/downloadRequestItemsExcel",
  async () => {
    try {
      // console.log("excel download handler..");
      const s3Url = `https://prism-public-assets.s3.ap-south-1.amazonaws.com/PRISM%20RFQ%20Template.xlsx`;
      
      // Create a temporary anchor element
      const anchor = document.createElement("a");
      anchor.href = s3Url;
      anchor.download = 'PRISM RFQ Template.xlsx'; // Set the desired file name
      anchor.click()
      return { status: "SUCCESS"};
    } catch (error: any) {
      return {
        error: error.response.data.message,
        status: error.response.status,
      };
    }
  }
);

export const getDashboardData = createAsyncThunk(
  "buyer/getDashboardData",
  async (parameter: any) => {
    const { axiosGet } = useCustomAxios();
    let url = `${API_ENDPOINTS.getDashboardData}`;

    Object.entries(parameter).forEach(
      ([key, value], index) =>
        (url = url + `${index > 0 ? "&" : "?"}${key}=${value}`)
    );

    try {
      const response = await axiosGet(url);
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return error.response.data;
    }
  }
);

// Buyer's Total Invoice Amount (invoices billed to the buyer) from the invoice
// service. Best-effort: on failure returns an empty object so the dashboard still
// renders the other metrics.
export const getBuyerInvoiceStats = createAsyncThunk(
  "buyer/getBuyerInvoiceStats",
  async (parameter: any) => {
    const { axiosGet } = useCustomAxios();
    let url = `${API_ENDPOINTS.getBuyerInvoiceStats}?perspective=buyer`;

    Object.entries(parameter).forEach(
      ([key, value]) => (url = url + `&${key}=${value}`)
    );

    try {
      const response = await axiosGet(url);
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return { data: {}, status: error?.response?.status };
    }
  }
);

// Derive the download filename from the presigned S3 URL, which ends with the
// real object name (e.g. RFQ0002.xlsx / RFQ0002.pdf) — so the saved file keeps
// the correct name and extension instead of a hardcoded one.
const getDownloadFileName = (downloadURL: string): string => {
  try {
    const name = decodeURIComponent(
      new URL(downloadURL).pathname.split("/").pop() || ""
    );
    return name || "rfq_document";
  } catch {
    return "rfq_document";
  }
};

export const getRfqPdf = createAsyncThunk(
  "buyer/getRfqPdf",
  async (id: string) => {
    const { axiosGet } = useCustomAxios();
    let url = API_ENDPOINTS.downloadRFQ;
    url = url.replace(":rfqId", id);
    try {
      const response = await axiosGet(url);
      const downloadURL = response.data.downloadURL;
      const downloadLink = document.createElement("a");
      document.body.appendChild(downloadLink);
      downloadLink.href = downloadURL;
      downloadLink.download = getDownloadFileName(downloadURL);
      downloadLink.target = "_blank";
      downloadLink.click();
      document.body.removeChild(downloadLink);
      return { data: response.data, status: response.status };
    } catch (error: any) {
      console.log(error);
      return error.response.data;
    }
  }
);


export const getRequestQouteApproval = createAsyncThunk(
  'buyer/getRequestApproval',
  async (requestId : string) => {
    const { axiosGet } = useCustomAxios();
    const url = `/order/request/${requestId}/quoteApproval`;
    
    try{
      const response = await axiosGet(url);
      return { data: response.data, status: response.status };
    }
    catch(error: any) {
      return error.response.data;
    }
  }
)
export const getAllProductTypesApi = createAsyncThunk(
  'order/productTypes/getAll',
  async () => {
    const { axiosGet } = useCustomAxios();
  
    try{
      const response = await axiosGet(API_ENDPOINTS.fetchAllProductTypes);
      return { data: response.data, status: response.status };
    }
    catch(error: any) {
      return error.response.data;
    }
  }
)

export const getRfqExcel = createAsyncThunk(
  "buyer/getRfqExcel",
  async (body: any, { rejectWithValue }) => {
    const { axiosPost } = useCustomAxios();
    let url = API_ENDPOINTS.downloadRFQExcel;
    try {
      const response = await axiosPost(url, body);
      const downloadURL = response.data.downloadURL;
      const downloadLink = document.createElement("a");
      document.body.appendChild(downloadLink);
      downloadLink.href = downloadURL;
      downloadLink.download = getDownloadFileName(downloadURL);
      downloadLink.target = "_blank";
      downloadLink.click();
      document.body.removeChild(downloadLink);
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getRequestProformaList = createAsyncThunk(
  "buyer/getRequestProformaList",
  async (parameter: any) => {
    const { axiosGet } = useCustomAxios();

    // This list was permanently empty. Three separate faults in the old
    // `url + (index > 0 ? "&" : "?") + key + "=" + value` loop:
    //
    // 1. `org: storePartyState.currentOrg` is null for a user with no current
    //    org, and String(null) sent the literal `org=null`, which the API
    //    rejects with 400. Null/undefined params are now omitted entirely
    //    rather than stringified.
    // 2. `Date` values (startDate/endDate/closeAfterDate) stringify to
    //    "Tue Sep 01 2026 00:00:00 GMT+0530 (India Standard Time)" - spaces,
    //    parentheses and a `+` interpolated raw into the query. They go out as
    //    ISO 8601 now, and every value is encoded, matching the same loop in
    //    inventory.ts / invoice.ts / party.ts.
    // 3. The `index > 0` test picked `?` vs `&` from the *original* position,
    //    so skipping any entry would have produced a second `?` or a leading
    //    `&`. The parts are joined instead.
    const query = Object.entries(parameter)
      .filter(([, value]) => value !== null && value !== undefined)
      .map(([key, value]) => {
        const serialised =
          value instanceof Date ? value.toISOString() : String(value);
        return `${encodeURIComponent(key)}=${encodeURIComponent(serialised)}`;
      })
      .join("&");

    const url = `${API_ENDPOINTS.getRequestProformaList}${
      query ? `?${query}` : ""
    }`;

    try {
      const response = await axiosGet(url);
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return error.response.data;
    }
  }
);